import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 76 (studio.header.tabs.${activeSection}; activeSection from StudioSection):
//   studio.header.tabs.drafts
//   studio.header.tabs.published
//   studio.header.tabs.published_galleries
//   studio.header.tabs.published_tiers
//   studio.header.tabs.scheduled
//   studio.header.tabs.stash
//   studio.header.tabs.playground
//   studio.header.tabs.literature
//   studio.header.tabs.journals
//   studio.header.tabs.bookmarks
//   studio.header.tabs.shop_reports
//   studio.header.tabs.orders_status
//   studio.header.tabs.orders_originals
//   studio.header.tabs.products_lobby
//   studio.header.tabs.products_tier
//   studio.header.tabs.products_tier_unlisted
//   studio.header.tabs.products_commission
//   studio.header.tabs.products_original
//   studio.header.tabs.products_original_unlisted
//   studio.header.tabs.products_premium_gallery
//   studio.header.tabs.products_pcp
//   studio.header.tabs.products_pcp_unlisted
//   studio.header.tabs.products_exclusive
//   studio.header.tabs.products_exclusive_unlisted
//   studio.header.tabs.seller_discounts_active
//   studio.header.tabs.seller_discounts_scheduled
//   studio.header.tabs.seller_discounts_ended
//   studio.header.tabs.seller_discounts_active_and_scheduled
//   studio.header.tabs.seller_discounts_all
//   studio.header.tabs.home
//   studio.header.tabs.apps
//   studio.header.tabs.apps_unlisted
//   studio.header.tabs.earnings
import { useSelector } from 'react-redux';
import { StudioSection } from '../../types/store';
import { getIsProductsSection } from '../../selectors/studio';
import StashHeader from './sections/StashHeader';
import PlaygroundHeader from './sections/PlaygroundHeader';
import DraftsHeader from './sections/DraftsHeader';
import PublishedHeader from './sections/PublishedHeader';
import ShopReportsHeader from './sections/ShopReportsHeader';
import BaseHeader from './sections/BaseHeader/BaseHeader';
import SimpleUploadButton from '../SimpleUploadButton';
import ProductsHeader from '../products/ProductsHeader';
import SellerDiscountsHeader from './sections/SellerDiscountsHeader';
import AppsHeader from './sections/AppsHeader';
import OrdersHeader from './sections/OrdersHeader';

export interface Props {
  activeSection: StudioSection;
  className?: string;
}

const StudioHeader: FC<Props> = ({ activeSection, className }) => {
  const { t } = useTranslation();
  const isProductsSection = useSelector(getIsProductsSection);
  const disableUpload =
    activeSection === StudioSection.PRODUCTS_LOBBY ||
    activeSection === StudioSection.EARNINGS;

  if (isProductsSection) {
    return <ProductsHeader className={className} />;
  }

  switch (activeSection) {
    case StudioSection.STASH:
      return <StashHeader className={className} />;
    case StudioSection.PLAYGROUND:
      return <PlaygroundHeader className={className} />;
    case StudioSection.SHOP_REPORTS:
      return <ShopReportsHeader className={className} />;
    case StudioSection.DRAFTS:
    case StudioSection.LITERATURE:
    case StudioSection.JOURNALS:
      return <DraftsHeader className={className} />;
    case StudioSection.PUBLISHED:
    case StudioSection.PUBLISHED_GALLERIES:
      return <PublishedHeader className={className} />;
    case StudioSection.SCHEDULED:
      return <DraftsHeader className={className} />;
    case StudioSection.SELLER_DISCOUNTS_ACTIVE:
    case StudioSection.SELLER_DISCOUNTS_SCHEDULED:
    case StudioSection.SELLER_DISCOUNTS_ENDED:
    case StudioSection.SELLER_DISCOUNTS_ALL:
      return <SellerDiscountsHeader className={className} />;
    case StudioSection.APPS:
    case StudioSection.APPS_UNLISTED:
      return <AppsHeader className={className} />;
    case StudioSection.ORDERS_STATUS:
    case StudioSection.ORDERS_ORIGINALS:
      return <OrdersHeader className={className} />;
    case StudioSection.EARNINGS:
      return (
        <BaseHeader
          className={className}
          title={t('studio.earnings.title')}
          subtitle={t('studio.earnings.subtitle')}
        />
      );
    case StudioSection.HOME:
      return null;
    default:
      return (
        <BaseHeader
          className={className}
          title={t(`studio.header.tabs.${activeSection.toLowerCase()}`)}
          actions={
            !disableUpload && (
              <SimpleUploadButton
                label={t('studio.header.upload')}
                linkToSubmit
              />
            )
          }
        />
      );
  }
};
StudioHeader.displayName = 'StudioHeader';

export default StudioHeader;
